import api from './config';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { logout } from './authSlice';

const normalizeErrors = (message) =>
  Array.isArray(message) ? message : message ? [message] : [];

const initialState = {
  claims: [],
  archivedClaims: [],
  claim: null,
  loading: false,
  errors: [],
  recordsTotal: 0,
  dashboardStats: null,
  statsLoading: false,
  statsError: null
};

export const fetchClaims = createAsyncThunk(
  'claims/fetchClaims',
  async (params, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/claim', { params });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchClaimByID = createAsyncThunk(
  'claims/fetchClaimByID',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(`/claim/getDetailClaims/${id}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const addMessage = createAsyncThunk(
  'claims/addMessage',
  async ({ id, message }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post(`/claim/${id}/add-message`, { message });
      await dispatch(fetchClaimByID(id));
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const addMessagetreated = createAsyncThunk(
  'claims/addMessagetreated',
  async ({ id, message }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post(`/claim/${id}/add_message_treated`, { message });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const AddMediaMessage = createAsyncThunk(
  'claims/AddMediaMessage',
  async ({ formData, id }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post(`/claim/${id}/add-files`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      dispatch(fetchClaimByID(id));
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(error.response?.data);
    }
  }
);

export const updateStatusClaim = createAsyncThunk(
  'claims/updateStatusClaim',
  async ({ id, status }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`/claim/${id}/${status}/update-status`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchArchivedClaimsByCompany = createAsyncThunk(
  'claims/fetchArchivedClaimsByCompany',
  async (companyId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(`/claim/archived/company/${companyId}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const unarchive = createAsyncThunk(
  'claims/unarchive',
  async (claimId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`/claim/${claimId}/unarchive`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const archive = createAsyncThunk(
  'claims/archive',
  async (claimId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`/claim/${claimId}/archive`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchClaimBarchivedAll = createAsyncThunk(
  'claims/fetchClaimBarchivedAll',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/claim/archivedAll/');
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const blockClient = createAsyncThunk(
  'claims/blockClient',
  async (claim, { rejectWithValue, dispatch }) => {
    try {
      await api.put(`/user/block/${claim.added_by[0]?._id}`);
      const result = await dispatch(fetchClaimByID(claim?._id));
      return result.payload;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const unblockClient = createAsyncThunk(
  'claims/unblockClient',
  async (claim, { rejectWithValue, dispatch }) => {
    try {
      await api.put(`/user/unblock/${claim.added_by[0]?._id}`);
      const result = await dispatch(fetchClaimByID(claim?._id));
      return result.payload;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteMessage = createAsyncThunk(
  'claims/deleteMessage',
  async ({ claimId, actionId }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.delete(`/claim/${claimId}/delete/${actionId}`);
      dispatch(fetchClaimByID(claimId));
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(error.response?.data || 'Error deleting message');
    }
  }
);

export const markActionsViewed = createAsyncThunk(
  'claims/markActionsViewed',
  async (claimId, { rejectWithValue, dispatch }) => {
    try {
      await api.patch(`/claim/${claimId}/actions-viewed`);
      const { payload } = await dispatch(fetchClaimByID(claimId));
      return payload;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(error.response?.data || 'Server Error');
    }
  }
);

export const fetchDashboardStats = createAsyncThunk(
  'claims/fetchDashboardStats',
  async ({ startDate, endDate } = {}, { rejectWithValue, dispatch }) => {
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const response = await api.get('/stats/dashboard', { params });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

const claimsSlice = createSlice({
  name: 'claims',
  initialState,
  reducers: {
    appendMessageToClaim: (state, action) => {
      if (!state.claim) return;
      state.claim.actions.push(action.payload);
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClaims.pending, (state) => { state.loading = true; })
      .addCase(fetchClaims.fulfilled, (state, { payload }) => {
        state.claims = payload?.data || [];
        state.recordsTotal = payload?.recordsTotal || 0;
        state.loading = false;
      })
      .addCase(fetchClaims.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload?.message);
        state.loading = false;
      })

      .addCase(fetchClaimByID.pending, (state) => { state.loading = true; })
      .addCase(fetchClaimByID.fulfilled, (state, { payload }) => {
        state.claim = payload;
        state.loading = false;
      })
      .addCase(fetchClaimByID.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload?.message);
        state.loading = false;
      })

      .addCase(addMessage.pending, (state) => { state.loading = true; })
      .addCase(addMessage.fulfilled, (state) => { state.loading = false; })
      .addCase(addMessage.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload?.message);
        state.loading = false;
      })

      .addCase(addMessagetreated.pending, (state) => { state.loading = true; })
      .addCase(addMessagetreated.fulfilled, (state, { payload }) => {
        state.loading = false;
        if (state.claim && payload?._id && state.claim._id === payload._id) {
          state.claim = payload;
        }
      })
      .addCase(addMessagetreated.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload?.message);
        state.loading = false;
      })

      .addCase(AddMediaMessage.pending, (state) => { state.loading = true; })
      .addCase(AddMediaMessage.fulfilled, (state) => { state.loading = false; })
      .addCase(AddMediaMessage.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload?.message);
        state.loading = false;
      })

      .addCase(updateStatusClaim.pending, (state) => { state.loading = true; })
      .addCase(updateStatusClaim.fulfilled, (state) => { state.loading = false; })
      .addCase(updateStatusClaim.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload?.message);
        state.loading = false;
      })

      .addCase(archive.pending, (state) => { state.loading = true; })
      .addCase(archive.fulfilled, (state) => { state.loading = false; })
      .addCase(archive.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload?.message);
        state.loading = false;
      })

      .addCase(unarchive.pending, (state) => { state.loading = true; })
      .addCase(unarchive.fulfilled, (state) => { state.loading = false; })
      .addCase(unarchive.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload?.message);
        state.loading = false;
      })

      .addCase(fetchArchivedClaimsByCompany.pending, (state) => { state.loading = true; })
      .addCase(fetchArchivedClaimsByCompany.fulfilled, (state, { payload }) => {
        state.archivedClaims = payload;
        state.loading = false;
      })
      .addCase(fetchArchivedClaimsByCompany.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload?.message);
        state.loading = false;
      })

      .addCase(blockClient.pending, (state) => { state.loading = true; })
      .addCase(blockClient.fulfilled, (state, { payload }) => {
        if (payload) state.claim = payload;
        state.loading = false;
      })
      .addCase(blockClient.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload?.message);
        state.loading = false;
      })

      .addCase(unblockClient.pending, (state) => { state.loading = true; })
      .addCase(unblockClient.fulfilled, (state, { payload }) => {
        if (payload) state.claim = payload;
        state.loading = false;
      })
      .addCase(unblockClient.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload?.message);
        state.loading = false;
      })

      .addCase(deleteMessage.pending, (state) => { state.loading = true; })
      .addCase(deleteMessage.fulfilled, (state) => { state.loading = false; })
      .addCase(deleteMessage.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload?.message);
        state.loading = false;
      })

      .addCase(markActionsViewed.pending, (state) => { state.loading = true; })
      .addCase(markActionsViewed.fulfilled, (state, { payload }) => {
        state.claim = payload;
        state.loading = false;
      })
      .addCase(markActionsViewed.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload?.message);
        state.loading = false;
      })

      .addCase(fetchClaimBarchivedAll.pending, (state) => { state.loading = true; })
      .addCase(fetchClaimBarchivedAll.fulfilled, (state, { payload }) => {
        state.archivedClaims = payload;
        state.loading = false;
      })
      .addCase(fetchClaimBarchivedAll.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload?.message);
        state.loading = false;
      })

      .addCase(fetchDashboardStats.pending, (state) => {
        state.statsLoading = true;
        state.statsError = null;
      })
      .addCase(fetchDashboardStats.fulfilled, (state, { payload }) => {
        state.statsLoading = false;
        state.dashboardStats = payload;
        state.statsError = null;
      })
      .addCase(fetchDashboardStats.rejected, (state, { payload }) => {
        state.statsLoading = false;
        state.statsError = payload?.message || 'Failed to fetch dashboard stats';
        state.dashboardStats = null;
      });
  }
});

export const { appendMessageToClaim } = claimsSlice.actions;

export default claimsSlice.reducer;
